import { useEffect, useState } from 'react';

declare global {
  interface Window {
    grecaptcha: any;
    RECAPTCHA_SITE_KEY: string;
  }
}

interface RecaptchaConfig {
  enabled: boolean;
  siteKey: string;
  scoreThreshold: number;
}

export const useRecaptcha = (config: RecaptchaConfig | null) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!config || !config.enabled || !config.siteKey) {
      setLoaded(true); // Consider it "loaded" but disabled
      return;
    }

    // Set global site key
    window.RECAPTCHA_SITE_KEY = config.siteKey;

    // Check if script already exists
    const existingScript = document.getElementById('recaptcha-script');
    if (existingScript) {
      existingScript.remove();
    }

    // Load reCAPTCHA script
    const script = document.createElement('script');
    script.id = 'recaptcha-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${config.siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      setLoaded(true);
      setError(null);
    };

    script.onerror = () => {
      setError('Failed to load reCAPTCHA');
      setLoaded(false);
    };

    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [config?.enabled, config?.siteKey]);

  const executeRecaptcha = async (action: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!config || !config.enabled) {
        // If reCAPTCHA is disabled, return dummy token
        resolve('disabled');
        return;
      }

      if (!loaded || typeof window.grecaptcha === 'undefined') {
        reject(new Error('reCAPTCHA has not loaded yet. Please refresh and try again.'));
        return;
      }

      try {
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(window.RECAPTCHA_SITE_KEY, { action })
            .then(resolve)
            .catch(reject);
        });
      } catch (err) {
        reject(err);
      }
    });
  };

  const verifyRecaptchaToken = async (token: string, action: string) => {
    if (!config || !config.enabled || token === 'disabled') {
      return { success: true, score: 1.0, message: 'reCAPTCHA disabled' };
    }

    try {
      const response = await fetch('/api/settings/test-recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, action }),
      });

      if (!response.ok) {
        throw new Error('Unable to validate reCAPTCHA. Please try again.');
      }

      return await response.json();
    } catch (error) {
      throw new Error('reCAPTCHA verification failed');
    }
  };

  return {
    loaded,
    error,
    executeRecaptcha,
    verifyRecaptchaToken,
    enabled: config?.enabled || false
  };
};
